import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, Mail, X, CheckCircle, RefreshCw } from 'lucide-react';

const DISMISS_KEY = 'fm_email_confirm_banner_dismissed_until';

const severityStyles = {
  info: {
    bg: 'rgba(245, 158, 11, 0.08)',
    border: '#f59e0b',
    color: '#92400e',
    iconColor: '#f59e0b',
  },
  warning: {
    bg: 'rgba(234, 88, 12, 0.10)',
    border: '#ea580c',
    color: '#9a3412',
    iconColor: '#ea580c',
  },
  critical: {
    bg: 'rgba(220, 38, 38, 0.10)',
    border: '#dc2626',
    color: '#991b1b',
    iconColor: '#dc2626',
  },
};

const EmailConfirmationBanner = () => {
  const [status, setStatus] = useState(null);
  const [resending, setResending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch('index.php/user/getConfirmationStatus', {
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      // Silent fail — Banner ist optional
    }
  }, []);

  useEffect(() => {
    loadStatus();

    // Dismissal-State aus localStorage lesen (24h)
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (until > Date.now()) setDismissed(true);
    } catch (e) {
      // localStorage evtl. nicht verfügbar
    }
  }, [loadStatus]);

  const handleResend = async () => {
    setResending(true);
    setFeedback(null);
    try {
      const res = await fetch('index.php/user/resendConfirmationEmail', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        setFeedback({ type: 'success', message: data.message || 'E-Mail gesendet.' });
        if (data.already_done) loadStatus();
      } else {
        setFeedback({ type: 'error', message: data.error || 'Versand fehlgeschlagen.' });
        if (data.already_done) loadStatus();
      }
    } catch (err) {
      setFeedback({ type: 'error', message: 'Netzwerkfehler. Bitte erneut versuchen.' });
    }
    setResending(false);
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch (e) {
      // ignore
    }
    setDismissed(true);
  };

  if (!status || !status.needs_confirmation) return null;

  const severity = status.severity || 'info';
  // critical (>=7 Tage): nicht dismissable, immer zeigen
  if (dismissed && severity !== 'critical') return null;

  const style = severityStyles[severity] || severityStyles.info;
  const Icon = severity === 'critical' ? AlertTriangle : Mail;

  const headline =
    severity === 'critical'
      ? 'E-Mail-Bestätigung dringend erforderlich'
      : severity === 'warning'
      ? 'Bitte bestätige deine E-Mail-Adresse'
      : 'E-Mail-Adresse noch nicht bestätigt';

  const subtext =
    severity === 'critical'
      ? `Tag ${status.days} ohne Bestätigung. Bitte bestätige deine E-Mail-Adresse ${status.email}, damit wichtige Mails (Passwort-Reset, Rechnungen) zuverlässig ankommen.`
      : severity === 'warning'
      ? `Wir haben einen Bestätigungslink an ${status.email} gesendet. Bitte klick ihn, damit wir sicherstellen können, dass wichtige E-Mails dich erreichen.`
      : `Wir haben einen Bestätigungslink an ${status.email} gesendet. Bitte klick ihn, um deine Registrierung abzuschließen.`;

  return (
    <div
      style={{
        background: style.bg,
        borderLeft: `4px solid ${style.border}`,
        padding: '0.875rem 1.25rem',
        margin: '0 0 0.75rem 0',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.875rem',
        fontSize: '0.875rem',
        color: style.color,
        borderRadius: '4px',
      }}
      role="alert"
    >
      <Icon size={20} style={{ color: style.iconColor, flexShrink: 0, marginTop: '2px' }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{headline}</div>
        <div style={{ fontSize: '0.8125rem', lineHeight: 1.5, color: style.color, opacity: 0.9 }}>
          {subtext}
        </div>

        {feedback && (
          <div
            style={{
              marginTop: '0.5rem',
              padding: '0.375rem 0.625rem',
              borderRadius: '4px',
              background: feedback.type === 'success' ? 'rgba(16,185,129,0.12)' : 'rgba(220,38,38,0.12)',
              color: feedback.type === 'success' ? '#065f46' : '#991b1b',
              fontSize: '0.8125rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
          >
            {feedback.type === 'success' && <CheckCircle size={14} />}
            {feedback.message}
          </div>
        )}

        <div style={{ marginTop: '0.625rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.375rem 0.875rem',
              fontSize: '0.8125rem',
              fontWeight: 600,
              border: `1px solid ${style.border}`,
              borderRadius: '6px',
              background: style.border,
              color: '#fff',
              cursor: resending ? 'default' : 'pointer',
              opacity: resending ? 0.6 : 1,
            }}
          >
            <RefreshCw size={14} className={resending ? 'spin' : ''} />
            {resending ? 'Wird gesendet…' : 'Bestätigungs-E-Mail erneut senden'}
          </button>

          {severity !== 'critical' && (
            <button
              type="button"
              onClick={handleDismiss}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.8125rem',
                border: 'none',
                background: 'transparent',
                color: style.color,
                cursor: 'pointer',
                opacity: 0.75,
              }}
            >
              Später erinnern
            </button>
          )}
        </div>
      </div>

      {severity !== 'critical' && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Schließen"
          style={{
            border: 'none',
            background: 'transparent',
            color: style.color,
            cursor: 'pointer',
            padding: '0.25rem',
            opacity: 0.6,
            flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default EmailConfirmationBanner;
